/**
 * Fiche client affichée sur l'écran client (Aerith, mode « Client ») : ce que la Caisse saisit s'affiche en direct pour que le client
 * vérifie qu'il n'y a pas de faute. Aucun dessin ici, seulement du calcul (donc testable) : libellés, regroupement, mise en forme
 * et détection du champ qui vient de changer.
 *
 * La Caisse envoie une fiche NOMMÉE {nom, prenom, email, ...} (voir CHAMPS pour les noms connus). L'ancien format, une simple liste
 * de valeurs sans nom, reste affiché tel quel (une ligne par valeur).
 */

/** Champs connus, dans l'ordre d'affichage : {cle, libelle, groupe, large?, essentiel?}. `essentiel` : demandé à chaque client en boutique (nom, prénom, téléphone), toujours affiché même vide ; les autres n'apparaissent que lorsqu'on commence à les saisir. `large` : valeur potentiellement longue, qui prend toute la largeur (les autres se rangent deux par deux). */
export const CHAMPS = [
    {cle: "entreprise", libelle: "Entreprise", groupe: "identite", large: true},
    {cle: "nom", libelle: "Nom", groupe: "identite", essentiel: true, large: true},
    {cle: "prenom", libelle: "Prénom", groupe: "identite", essentiel: true, large: true},
    {cle: "email", libelle: "E-mail", groupe: "contact", type: "email", large: true},
    {cle: "telephone", libelle: "Téléphone portable", groupe: "contact", type: "telephone", essentiel: true},
    {cle: "fixe", libelle: "Téléphone fixe", groupe: "contact", type: "telephone"},
    {cle: "destinataire", libelle: "Destinataire", groupe: "adresse", large: true},
    {cle: "adresse", libelle: "Adresse", groupe: "adresse", large: true},
    {cle: "adresse2", libelle: "Complément d'adresse", groupe: "adresse", large: true},
    {cle: "codepostal", libelle: "Code postal", groupe: "adresse"},
    {cle: "ville", libelle: "Ville", groupe: "adresse"},
];

export const GROUPES = [
    {id: "identite", titre: "Vous"},
    {id: "contact", titre: "Pour vous joindre"},
    {id: "adresse", titre: "Votre adresse"},
    {id: "autres", titre: "Autres informations"},
];

/** Texte propre d'une valeur : chaînes et nombres seulement (booléens, null et objets n'ont rien à montrer), espaces de bord retirés. */
export function texte(valeur) {
    if (typeof valeur === "number" && Number.isFinite(valeur)) return String(valeur);
    return typeof valeur === "string" ? valeur.replace(/\s+/g, " ").trim() : "";
}

/**
 * Numéro de téléphone lisible. Français à 10 chiffres : « 06 12 34 56 78 » ; international +33 : « +33 6 12 34 56 78 ».
 * Tout autre format est laissé tel que saisi (la Caisse peut écrire ce qu'elle veut, l'écran ne corrige pas).
 */
export function formaterTelephone(valeur) {
    const brut = texte(valeur);
    if (!/^[+\d\s.\-()]*$/.test(brut)) return brut;
    const chiffres = brut.replace(/\D/g, "");
    if (/^\+/.test(brut) && /^33\d{9}$/.test(chiffres)) return "+33 " + chiffres.slice(2).replace(/^(\d)(\d{2})(\d{2})(\d{2})(\d{2})$/, "$1 $2 $3 $4 $5");
    if (!/^\+/.test(brut) && /^0\d{9}$/.test(chiffres)) return chiffres.replace(/(\d{2})(?=\d)/g, "$1 ");
    return brut;
}

/** Libellé lisible d'un nom de champ inconnu : « code_client » devient « Code client ». */
export function libelleInconnu(cle) {
    const s = String(cle).replace(/[_-]+/g, " ").trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * La fiche prête à afficher : { liste: bool, champs: [{cle, libelle, groupe, valeur, vide}] }.
 * - fiche nommée : nom, prénom et téléphone toujours (même vides, marqués « vide » : le client voit ce qui reste à renseigner) ; les
 *   autres champs connus (e-mail, adresse, entreprise...) seulement quand ils contiennent quelque chose ; puis les inconnus non vides
 *   (groupe « autres ») ;
 * - ancienne liste de valeurs : une ligne par valeur non vide, sans libellé.
 */
export function fiche(donnees) {
    if (Array.isArray(donnees)) {
        const valeurs = donnees.map(texte).filter(v => v !== "");
        return {liste: true, champs: valeurs.map((valeur, i) => ({cle: "ligne" + i, libelle: null, groupe: "autres", valeur, vide: false}))};
    }
    if (!donnees || typeof donnees !== "object" || Object.keys(donnees).length === 0) return {liste: false, champs: []};

    // dès qu'une fiche est envoyée, les champs essentiels sont là (même vides : le client voit ce qui reste à saisir) ; les autres
    // n'apparaissent qu'une fois commencés, pour ne pas montrer de rubriques que la boutique n'utilise presque jamais
    const champs = CHAMPS.map(c => {
        const brut = texte(donnees[c.cle]);
        const valeur = c.type === "telephone" ? formaterTelephone(brut) : brut;
        return {cle: c.cle, libelle: c.libelle, groupe: c.groupe, valeur, vide: valeur === "", large: !!c.large, type: c.type || "texte", essentiel: !!c.essentiel};
    }).filter(c => c.essentiel || !c.vide);
    Object.keys(donnees).filter(cle => !CHAMPS.some(c => c.cle === cle)).forEach(cle => {
        const valeur = texte(donnees[cle]);
        if (valeur !== "") champs.push({cle, libelle: libelleInconnu(cle), groupe: "autres", valeur, vide: false, large: true, type: "texte"});
    });
    return {liste: false, champs};
}

/** Les champs regroupés par rubrique, dans l'ordre de GROUPES ; une rubrique sans champ n'apparaît pas. */
export function groupes(champs) {
    return GROUPES
        .map(g => ({...g, champs: champs.filter(c => c.groupe === g.id)}))
        .filter(g => g.champs.length > 0);
}

/** Clés des champs dont la valeur affichée a changé entre deux envois (un champ qui apparaît compte aussi). */
export function champsModifies(avant, apres) {
    const ancien = {};
    fiche(avant).champs.forEach(c => { ancien[c.cle] = c.valeur; });
    return fiche(apres).champs.filter(c => (ancien[c.cle] || "") !== c.valeur).map(c => c.cle);
}

/** Prénom à saluer : le prénom saisi, sinon rien. */
export function prenomAAccueillir(donnees) {
    if (!donnees || Array.isArray(donnees) || typeof donnees !== "object") return "";
    return texte(donnees.prenom);
}

/** Vrai quand il n'y a encore rien à montrer (aucune valeur saisie nulle part). */
export const ficheVide = donnees => fiche(donnees).champs.every(c => c.vide);

/** Nombre de champs jusqu'auquel la fiche est « grande » (un champ par ligne, très gros caractères) : le cas ordinaire de la boutique. */
export const SEUIL_GRAND = 4;
/** Nombre de champs à partir duquel la fiche est « dense » : l'écran réduit un peu les textes pour que tout tienne sans défiler. */
export const SEUIL_DENSE = 9;

/**
 * Taille d'affichage selon la quantité à montrer : « grand » (4 champs ou moins : nom, prénom, téléphone, et au plus un champ de plus),
 * « normal » (5 à 8) ou « dense » (9 ou plus).
 */
export function tailleFiche(champs) {
    if (champs.length <= SEUIL_GRAND) return "grand";
    return champs.length >= SEUIL_DENSE ? "dense" : "normal";
}

/**
 * Morceaux d'une valeur où l'écran peut passer à la ligne : on coupe APRÈS un @, un point, un tiret ou un souligné, jamais au milieu d'un
 * mot (« lea.marie@ / exemple- / entreprise.fr » plutôt que « exempl / e-entreprise »). Les morceaux remis bout à bout redonnent la valeur.
 */
export function morceauxCoupables(valeur) {
    const morceaux = [];
    let courant = "";
    for (const lettre of String(valeur)) {
        courant += lettre;
        if ("@.-_".includes(lettre)) {
            morceaux.push(courant);
            courant = "";
        }
    }
    if (courant) morceaux.push(courant);
    return morceaux;
}
